import { useEffect, useState, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Box, Card } from '@mui/material';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { CategoryNavbar } from './order-navbar';
import { CATALOG } from '../../../../reusable-components/datagrid/shopColumns';
import { useSelector } from '../../../../redux/store';
import QuantityField from './QuantityField';
import { createColumns } from '../../../../reusable-components/datagrid/custom-filters';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import Scrollbar from '../../../../reusable-components/scrollbar';
import { CollapseSidebar } from '../../../../reusable-components/collapse-sidebar';
import BudgetBox from './BudgetBox';
import ItemsGrid from './ItemsGrid';
import CategoryViewHeader from './CategoryViewHeader';
import { ShopCartContext, ShopDataContext } from './_ShopViewContext';
import { catalogCategoryFilter } from '../../admin/catalog/catalog-custom-filter';

export default function CatalogView() {
  const hasBudgetViewAccess = useScopeCheck(['View-Order'], true);
  const { facilityId = '' } = useParams();
  const [sortModel, setSortModel] = useState([
    { field: 'quantity', sort: 'desc' },
    { field: 'previouslyOrderedItems', sort: 'desc' },
  ]);
  const [initialSort, setInitialSort] = useState(true);

  const { data: { items, budgets, hasNoBudget } } = useSelector((state) => state.orderSheet);
  const isLoading = useSelector((state) => state.orderSheet.isLoading)

  const { data, setData } = useContext(ShopDataContext);

  useEffect(() => {
    setData(items.map((item) => ({ ...item, quantity: null })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (data.length > 0 && initialSort) {
      setSortModel([
        { field: 'quantity', sort: 'desc' },
        { field: 'previouslyOrderedItems', sort: 'desc' },
      ]);
      setInitialSort(false);
    }
  }, [data.length, initialSort]);

    const customToolbar = useCallback(
      () => <CategoryNavbar />,
      []
    );

  const quantity = {
    field: 'quantity',
    headerName: 'Qty',
    flex: 0.5,
    minWidth: 80,
    renderCell: (params) => (
      <QuantityField
        row={params.row}
        callback={(val) => {
          params.row.quantity = val;
        }}
      />
    ),
    valueGetter: (params) => params.row?.quantity ?? 0,
  };

  const columns = createColumns([...CATALOG, quantity]);
  const [openSidebar, setOpenSidebar] = useState(true);
  const [view, setView] = useState(localStorage.getItem('orders-new-order-shop-view-list-style') ?? 'list');

  return (
    <>
      <CategoryViewHeader view={view} setView={setView} />
      <Grid item xl={12}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
          <Grid item xs={hasBudgetViewAccess ? (openSidebar ? 9 : 11.9) : 12}>
            <Grid item xs={12}>
              {view === 'grid' ? (
                <ItemsGrid />
              ) : (
                <CustomDataGrid
                  gridId="orders-new-order-catalog-view"
                  boxSX={{ height: 'calc(100vh - 220px)' }}
                  data={data}
                  gridColumns={columns}
                  CustomLeftToolbar={customToolbar}
                  getRowId={(row) => row.id}
                  disableSelectionOnClick
                  sort={sortModel}
                  isLoading={isLoading}
                  customFilter={catalogCategoryFilter}
                />
              )}
            </Grid>
          </Grid>
          {hasBudgetViewAccess && (
            <Grid item xs={openSidebar ? 3 : 0.1} style={{ alignSelf: 'stretch', position: 'relative' }}>
              <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                <CollapseSidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar}>
                  {openSidebar && (
                    <>
                      <ScopeGuard scopes={['View-Order']} allowAdmin>
                        <Box>
                          <Grid item xs={12}>
                            {hasNoBudget ? (
                              <Card sx={{ p: 2, ml: 2, mt: 4 }}>No budget set. Contact your admin.</Card>
                            ) : budgets?.length > 0 ? (
                              <Grid item sx={{ mt: 2, position: 'relative' }}>
                                <Scrollbar sx={{ height: 'calc(100vh - 170px)' }}>
                                  <Grid
                                    container
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{ pb: '50px' }}
                                  >
                                    <Grid item>                                            
                                      <ScopeGuard scopes={['View-Order']} allowAdmin>
                                        {budgets.map((budget) => (
                                          <Box key={budget.agoraCategoryId} sx={{my: 1}}>
                                          <CartBudgetBox
                                            catId={budget.agoraCategoryId}
                                            facId={facilityId}
                                          />
                                          </Box>
                                        ))}
                                      </ScopeGuard>
                                    </Grid>
                                  </Grid>
                                </Scrollbar>
                              </Grid>
                            ) : (
                              <Card sx={{ p: 2, ml: 2, mt: 4 }}>Loading budget...</Card>
                            )}
                          </Grid>
                        </Box>
                      </ScopeGuard>
                    </>
                  )}
                </CollapseSidebar>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
}

function CartBudgetBox({ catId, facId }) {
  const { cart: cartData } = useContext(ShopCartContext);
  const { data } = useContext(ShopDataContext);
  const dataFromCategory =  data.filter(x => x.productDetails?.length && x.displayCategory?.id === catId && (x.quantity > 0 || cartData[x.id] > 0));
  return <BudgetBox
    cart={dataFromCategory.map(x => ({
      quantity: cartData[x.id] !== undefined ? cartData[x.id] : x.quantity,
      agoraCategoryId: x.displayCategory?.id,
      agoraSubcategoryId: x.displaySubcategory?.id,
      price: x.productDetails[0].price
    }))}
    catId={catId}
    facId={facId}
  />
}